import { captureException, captureMessage } from "@sentry/react";
import { useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { trackPurchaseEvent } from "~/analytics/eventTracker";
import useModal from "~/components/modal/Modal";
import ErrorModal from "~/components/modal/modals/ErrorModal/ErrorModal";
import {
  Button,
  Card,
  Checkbox,
  DefaultBlockContent,
  ExternalLink,
  Icon,
} from "~/components/shared";
import type { CustomerType } from "~/models/CustomerType";
import { ProductType } from "~/models/product/ProductType";
import { toBasketProduct } from "~/store/basket/basket.helpers";
import { getCampaign, mapCampaignData } from "~/store/campaign/api";
import { BYOD_PRODUCT_ID } from "~/store/campaign/byod.constants";
import { splitStreetAddresses } from "~/utils/address";
import { getValidDate } from "~/utils/validation";
import {
  CheckoutError,
  type CheckoutRequest,
  checkout as checkoutRequest,
} from "../../../../store/checkout/api";
import "../../campaign/summary/summary.scss";
import { NgtAlertForSummary } from "../../internet/components/NgtAlertForSummary";
import { useInternetFlowStore } from "../../internet/useInternetStore";
import { campaignRoutes } from "../campaign.routes";
import type { MappedCampaign } from "../campaign.utils";
import { SimpleSummary } from "../components/simple-summary/SimpleSummary";
import SimpleTotal from "../components/simple-total/SimpleTotal";
import { defaultDwellingUnit, defaultPartnerAlias } from "../constants";
import { useBasket } from "../useBasket";
import { toCheckoutProducts } from "./checkout.utils";

export const Summary: React.FC = () => {
  const campaign = useOutletContext<MappedCampaign>();

  const selectedProducts = useBasket((s) => s.selectedProducts);
  const contactInfo = useBasket((s) => s.contactInfo);
  const hasInternetProduct = useBasket((s) => s.hasInternetProduct)();

  const mainSubscription = selectedProducts.find(
    (product) => product.productType === ProductType.Subscription,
  );

  const {
    acceptedTerms,
    isCheckoutAttempted,
    isDelayedActivation,
    onCheckout,
    setAcceptedTerms,
  } = useCheckout(campaign);

  if (!contactInfo || !mainSubscription) {
    // This is already checked in step verification, see StepNavigation
    // So here we only do it for typescript
    return null;
  }

  const hideShipping = selectedProducts.some((p) => p.id === BYOD_PRODUCT_ID);
  const paidInstallationProducts = selectedProducts.filter(
    (p) => p.productType === ProductType.Installation && p.pricing.price !== 0,
  );
  const termsError =
    isCheckoutAttempted && !acceptedTerms
      ? "Vilkår og personvernerklæring må godtas for å kunne fullføre bestillingen"
      : undefined;

  return (
    <div>
      <div className="summary">
        <section className="section">
          <Card>
            <h2 className="title-3">
              <Icon type="cart" flip="horizontal" size={1.5} />
              Se over og fullfør bestillingen
            </h2>
            <SimpleSummary
              selectedProducts={selectedProducts}
              hideShipping={hideShipping}
            />
          </Card>
          <Card color="gray-dark">
            <SimpleTotal selectedProducts={selectedProducts} />
          </Card>
          {campaign.additionalTerms && (
            <div className="additional-terms">
              <DefaultBlockContent blocks={campaign.additionalTerms} />
            </div>
          )}
        </section>
        <section className="section instructions-and-conditions">
          {/* PAYMENT */}
          <h2 className="title-3">
            <Icon type="formCheck" size={1.5} />
            {isDelayedActivation ? "Aktivering og betaling" : "Betaling"}
          </h2>
          <p>
            {isDelayedActivation
              ? "TV-pakken aktiverer du enkelt på Min side når du er klar til å se TV. Abonnementskostnaden begynner ikke å løpe før du har aktivert TV-pakken. Faktura for utstyret mottar du sammen med utstyret i posten. "
              : "Første faktura kommer i posten. "}
            Du kan sette opp AvtaleGiro eller eFaktura på Min side. Da slipper
            du fakturagebyr og sparer penger og miljø.
          </p>
          {hasInternetProduct && (
            <NgtAlertForSummary className="ngt-payment-alert" />
          )}
          {/* DELIVERY */}
          <h2 className="title-3">
            <Icon type="box" size={1.5} />
            Levering
          </h2>
          <p className="delivery-lead">Faktura og utstyr vil bli sendt til:</p>
          <Card>
            <div
              className="delivery-address button-or-bold"
              data-testid="summary-delivery-address"
            >
              <p>
                {contactInfo.firstName} {contactInfo.lastName}
              </p>
              <p>{contactInfo.street}</p>
              <p>
                {contactInfo.zip} {contactInfo.city}
              </p>
            </div>
          </Card>
          <p>
            Du kan hente utstyret på Posten, eller velge gratis hjemlevering.
            Dette valget får du når du mottar en SMS fra Bring.
          </p>
          {/* INSTALLATION PRODUCTS INFO */}
          {paidInstallationProducts.length > 0 && (
            <>
              <h2 className="title-3">
                <Icon type="wrench" size={1.5} />
                Montering
              </h2>
              <p>
                Montøren vil ta kontakt med deg i løpet av noen dager for å
                avtale montering.
              </p>
            </>
          )}
          {/* TERMS */}
          <h2 className="title-3">
            <Icon type="form" size={1.5} />
            Forutsetninger og vilkår
          </h2>
          <p>
            Tilbudet forutsetter nytegning av et{" "}
            <b>{mainSubscription?.displayData.name}</b>-abonnement med 12
            måneders binding. Det er 14 dagers angrefrist og du kan si opp
            abonnementet på Min side etter 9 mnd. Oppsigelsestid er inneværende,
            pluss to måneder.
          </p>
          <Card>
            <Checkbox
              checked={acceptedTerms}
              label={<TermsLabel />}
              id="termsAndConditions"
              onChange={() => setAcceptedTerms(!acceptedTerms)}
              error={termsError}
              size={2}
              value="Godtar betingelser"
            />
          </Card>

          <Button
            type="primary"
            className="checkout-button"
            onClick={onCheckout}
          >
            Fullfør Bestilling
          </Button>
        </section>
      </div>
    </div>
  );
};

const TermsLabel = () => {
  const hasInternetProduct = useBasket((s) => s.hasInternetProduct)();

  return (
    <div>
      Jeg har lest og godtar {""}
      <ExternalLink
        className="link-style"
        href="https://www.rikstv.no/produkter/vilkar/abonnementsvilkar"
      >
        RiksTVs vilkår{" "}
      </ExternalLink>{" "}
      og {""}
      <ExternalLink
        className="link-style"
        href="https://www.rikstv.no/personvern"
      >
        personvernerklæring{" "}
      </ExternalLink>
      {hasInternetProduct && (
        <>
          samt{" "}
          <ExternalLink
            className="link-style"
            href="https://cdn.sanity.io/files/jakksh72/development/753506a70cf1635bc4f9fb1143846de6c2d7df0e.pdf"
          >
            NextGenTels vilkår{" "}
          </ExternalLink>
        </>
      )}
    </div>
  );
};

const useCheckout = (originalCampaign: MappedCampaign) => {
  const { open } = useModal();
  const navigate = useNavigate();
  const { campaignUrl } = useParams();
  const { selectedProducts, contactInfo, resetVerification, ...basketState } = useBasket();

  const [isCheckoutAttempted, setIsCheckoutAttempted] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const getInternetServiceOrder = useInternetFlowStore(
    (s) => s.getInternetServiceOrder,
  );

  const isDelayedActivation = basketState.useDelayedActivation;

  const onCheckout = async () => {
    if (!contactInfo) return;

    setIsCheckoutAttempted(true);
    if (!acceptedTerms || isCheckoutLoading) {
      return;
    }
    setIsCheckoutLoading(true);

    // resolve correct campaign now that we have potential other ISP partner
    const campaign =
      basketState.selectedISP?.partnerAlias === defaultPartnerAlias
        ? originalCampaign
        : await getCampaign(
            basketState.selectedISP?.partnerAlias || "",
            defaultDwellingUnit,
            originalCampaign.url,
          )
            .then((data) => mapCampaignData(data))
            .catch((err) => {
              captureException(err, {
                fingerprint: ["bestill_checkout_error_get_campaign"],
                extra: {
                  partnerAlias: basketState.selectedISP?.partnerAlias || "",
                  campaignUrl: originalCampaign.url,
                },
              });
            });

    if (!campaign) {
      open(<ErrorModal />);
      setIsCheckoutLoading(false);
      return;
    }

    const { street, houseNo, houseNoAlpha } = splitStreetAddresses(
      contactInfo.street,
    );
    // biome-ignore lint/style/noNonNullAssertion: We verify that this value is set in StepNavigation
    const partner = basketState.selectedISP!;
    const isoBirthDateNoMs = (
      getValidDate(contactInfo.birthDate)?.toISOString() ||
      new Date(0).toISOString()
    ).replace(".000Z", "Z");
    const checkoutRequestData: CheckoutRequest = {
      customerType: campaign.customerType as CustomerType,
      customerVerificationDetails: {
        provider: basketState.selectedVerificationProvider,
        handle: basketState.verificationResponse?.handle || "",
      },
      potentialCustomer: {
        email: contactInfo.email,
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        mobilePhone: contactInfo.mobilePhone,
        birthDate: isoBirthDateNoMs,
      },
      partnerAlias: partner.partnerAlias,
      trackingCode: campaign.campaignCode || "",
      defaultAddress: {
        city: contactInfo.city,
        street: street,
        zip: contactInfo.zip,
        houseNo: houseNo,
        houseNoAlpha: houseNoAlpha,
      },
      isLaterActivation: isDelayedActivation,
      laterActivationDate: basketState.delayedActivationDate
        ? new Date(basketState.delayedActivationDate).toISOString()
        : undefined,
      products: toCheckoutProducts(
        selectedProducts,
        basketState.flexipakkenChoice,
      ),
    };
    // This should be defined if the user has an internet product in the basket
    const internetServiceOrder = getInternetServiceOrder();
    if (internetServiceOrder) {
      checkoutRequestData.internetServiceOrder = internetServiceOrder;
    } else if (
      selectedProducts.some((p) => p.productType === ProductType.Internet)
    ) {
      // Something is wrong here, we should have had a internet service order since fiber product is part of basket
      captureMessage(
        "Missing internet service order with fiber product in basket",
        {
          fingerprint: ["missing internet service order"],
        },
      );
    }
    try {
      const recieptId = await checkoutRequest(checkoutRequestData);
      const basketProducts = selectedProducts.map((p) =>
          toBasketProduct(p, campaign.customerType as CustomerType),
        );
        trackPurchaseEvent({
          currency: "NOK",
          partnerName: partner.partnerName,
          transaction_id: recieptId,
          value: basketProducts.reduce(
            (total, product) => total + product.price,
            0,
          ),
          verification_method: basketState.selectedVerificationProvider,
          tax: 0,
          shipping: 0,
          coupon: campaign.campaignCode,
          products: basketProducts,
          funnelVersion: 2,
          delayedActivation: isDelayedActivation,
        });
        const searchParams = new URLSearchParams();
        searchParams.set(
          "subscription",
          selectedProducts.find(
            (p) => p.productType === ProductType.Subscription,
          )?.displayData.name || "",
        );
        if (basketState.flexipakkenChoice) {
          searchParams.set("flexipakkenChoice", basketState.flexipakkenChoice.name);
        }
        selectedProducts
          .filter((p) => p.productType === ProductType.Premium)
          .map((p) => p.displayData.name)
          .forEach((name) => {
            searchParams.append("premiumProducts", name);
          });
        selectedProducts
          .filter((p) => p.productType === ProductType.Internet)
          .forEach((p) => {
            searchParams.append("internetProducts", p.displayData.name);
          });

        navigate(`/bekreftelse?${searchParams.toString()}`);
    } catch (err) {
      setIsCheckoutLoading(false);

      if (err instanceof CheckoutError && err.code === "verification_expired") {
        // Recoverable: send the customer back to the step where they pick Vipps or BankID.
        // resetVerification clears the stale handle, so that step shows the provider choice
        // again instead of the contact form.
        resetVerification();
        navigate(`/${campaignUrl}/${campaignRoutes.campaignContactInfo}`);
        open(
          <ErrorModal message="Verifiseringen din har utløpt. Du må verifisere deg på nytt for å fullføre bestillingen." />,
        );
        captureMessage("Verification expired at checkout", { level: "warning" });
        return;
      }

      if (err instanceof CheckoutError && err.code === "verification_unavailable") {
        // Ours to fix, and the customer's verification is probably still good: let them retry.
        open(
          <ErrorModal message="Vi får ikke bekreftet verifiseringen din akkurat nå. Prøv igjen om litt." />,
        );
        captureException(new Error("Verification store unavailable at checkout", { cause: err }), {
          fingerprint: ["bestill_checkout_verification_unavailable"],
        });
        return;
      }

      if (err instanceof CheckoutError && err.code === "verification_failed") {
        // The form fields are read-only, so this points at a comparison bug or a tampered request.
        // Verifying again would not help.
        open(<ErrorModal />);
        captureException(new Error("Verification mismatch at checkout", { cause: err }), {
          fingerprint: ["bestill_checkout_verification_mismatch"],
        });
        return;
      }

      open(<ErrorModal />);
      captureException(new Error("Post Checkout Failed", { cause: err }), {
        fingerprint: ["bestill_checkout_error"],
      });
    }
  };

  return {
    onCheckout,
    isCheckoutAttempted,
    acceptedTerms,
    setAcceptedTerms,
    isDelayedActivation,
  };
};
